import React, { useState } from "react";
import { Link } from "@/lib/router-compat";
import {
  ArrowRight,
  Mail,
  CheckCircle2,
  ChevronDown,
  ChevronUp,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { postTrackingEvent, buildTrackingPayload } from "@/lib/tracking";

export interface NewsHeadline {
  date: string;
  title: string;
  href: string;
  category?: string;
}

export const defaultNewsHeadlines: NewsHeadline[] = [
  {
    date: "OCTOBER 14, 2026",
    title:
      "When Anxiety Turns Into Depression: A Guide for Founders and Executives Who Used to Be the Strong One",
    href: "/when-anxiety-turns-into-depression-a-guide-for-founders-and-executives",
    category: "For High Performers",
  },
  {
    date: "OCTOBER 14, 2026",
    title:
      "What Exists Now That Didn't Exist When You Were Raising Children: A Grandparent's Guide to Modern Depression Treatment",
    href: "/grandparents-guide-to-modern-depression-treatment",
    category: "For Grandparents",
  },
  {
    date: "OCTOBER 14, 2026",
    title:
      "How to Help Your Adult Child Through Depression Without Taking Over",
    href: "/help-your-adult-child-through-depression-without-taking-over",
    category: "For Grandparents",
  },
  {
    date: "OCTOBER 14, 2026",
    title:
      "You're a Grandparent Watching Your Child and Grandchildren Struggle, and You Feel Helpless. You're Not.",
    href: "/grandparent-watching-your-family-struggle-youre-not-helpless",
    category: "For Grandparents",
  },
  {
    date: "OCTOBER 14, 2026",
    title:
      'What to Have Ready: A Resource Guide for the Executive Assistant Who\'s Been Asked "What Would I Even Do?"',
    href: "/what-to-have-ready-resource-guide-executive-assistant",
    category: "For Executive Assistants",
  },
  {
    date: "OCTOBER 14, 2026",
    title:
      "How to Tell Your Boss You're Worried About Them (Without Losing Your Job or Their Trust)",
    href: "/how-to-tell-your-boss-youre-worried",
    category: "For Executive Assistants",
  },
  {
    date: "OCTOBER 14, 2026",
    title:
      "You're the Executive Assistant. You're Watching Your Boss Fall Apart. Here's What You're Actually Seeing.",
    href: "/watching-your-boss-fall-apart",
    category: "For Executive Assistants",
  },
  {
    date: "OCTOBER 14, 2026",
    title:
      "Diagnosed With Depression in the Second Half of Life: What Happens Next Is Up to You",
    href: "/depression-in-the-second-half-of-life",
    category: "For Adults",
  },
  {
    date: "OCTOBER 14, 2026",
    title:
      "Diagnosed With Depression at 80: It Is Not Too Late, and It Is Not Just Age",
    href: "/diagnosed-with-depression-at-80",
    category: "For Older Adults",
  },
  {
    date: "OCTOBER 14, 2026",
    title:
      "Diagnosed With Depression at 70: You Have Every Right to Feel Better",
    href: "/diagnosed-with-depression-at-70",
    category: "For Older Adults",
  },
  {
    date: "OCTOBER 14, 2026",
    title: 'Diagnosed With Depression at 60: This Is Not "Just Getting Older"',
    href: "/diagnosed-with-depression-at-60",
    category: "For Adults",
  },
  {
    date: "OCTOBER 14, 2026",
    title:
      "Diagnosed With Depression at 50: You Didn't Wait Too Long. You're Right on Time.",
    href: "/diagnosed-with-depression-at-50",
    category: "For Adults",
  },
  {
    date: "OCTOBER 14, 2026",
    title:
      "Diagnosed With Depression at 40: This Isn't Midlife. This Is Treatable.",
    href: "/diagnosed-with-depression-at-40",
    category: "For Adults",
  },
  {
    date: "OCTOBER 14, 2026",
    title:
      "Diagnosed With Depression at 30: You're Not Behind. You're Finally Getting Answers.",
    href: "/diagnosed-with-depression-at-30",
    category: "For Adults",
  },
  {
    date: "OCTOBER 14, 2026",
    title:
      "Diagnosed With Depression at 25: What Your Twenties Can Still Look Like",
    href: "/diagnosed-with-depression-at-25",
    category: "For Young Adults",
  },
  {
    date: "OCTOBER 14, 2026",
    title:
      "You're a Young Adult Who Was Just Diagnosed With Depression. This Is the Beginning, Not the End.",
    href: "/diagnosed-with-depression-as-a-young-adult",
    category: "For Young Adults",
  },
  {
    date: "OCTOBER 14, 2026",
    title:
      "You're a Teenager and You Were Just Diagnosed With Depression. Here's What That Actually Means.",
    href: "/diagnosed-with-depression-as-a-teenager",
    category: "For Teens",
  },
  {
    date: "OCTOBER 14, 2026",
    title:
      "You've Tried Everything Money Can Buy. Here's What You Haven't Tried.",
    href: "/youve-tried-everything-money-can-buy",
    category: "For Those Who've Tried Everything",
  },
  {
    date: "OCTOBER 14, 2026",
    title: "How to Do Due Diligence on a Depression Treatment",
    href: "/how-to-do-due-diligence-on-a-depression-treatment",
    category: "Patient Guide",
  },
  {
    date: "OCTOBER 14, 2026",
    title: "The Executive Who Can't Afford Six Weeks",
    href: "/the-executive-who-cant-afford-six-weeks",
    category: "For High Performers",
  },
  {
    date: "OCTOBER 14, 2026",
    title:
      "The Breakup Nobody Takes Seriously: Ending a Long-Term Relationship in Your 30s and 40s",
    href: "/ending-a-long-term-relationship-in-your-30s-and-40s",
    category: "For Adults",
  },
  {
    date: "OCTOBER 14, 2026",
    title:
      "Divorce and Depression: When the Hardest Year of Your Life Doesn't End",
    href: "/divorce-and-depression",
    category: "For Adults",
  },
  {
    date: "OCTOBER 14, 2026",
    title:
      "When a Breakup Doesn't Get Better: Heartbreak, Depression, and the Line Between Them",
    href: "/when-a-breakup-doesnt-get-better",
    category: "For Young Adults",
  },
  {
    date: "OCTOBER 14, 2026",
    title: "Your Child Got Into an Ivy. Here's What Nobody Warned You About.",
    href: "/your-child-got-into-an-ivy-what-nobody-warned-you-about",
    category: "For Parents",
  },
  {
    date: "OCTOBER 14, 2026",
    title:
      "Depression in Women: Why It's Twice as Common, Why It's Often Undertreated, and What's Changed",
    href: "/depression-in-women",
    category: "For Women",
  },
  {
    date: "OCTOBER 14, 2026",
    title:
      "Depression in College: Why the 'Best Years of Your Life' Can Be the Hardest, and What Actually Works",
    href: "/depression-in-college",
    category: "For Students & Families",
  },
  {
    date: "OCTOBER 14, 2026",
    title:
      "Teen Depression: How to Tell the Difference Between a Hard Year and a Real Illness",
    href: "/teen-depression-hard-year-or-real-illness",
    category: "For Families",
  },
  {
    date: "OCTOBER 14, 2026",
    title:
      "Is My Child Depressed? What Parents Should Know, and Why Your Own Depression Is Part of the Answer",
    href: "/is-my-child-depressed-what-parents-should-know",
    category: "For Families",
  },
  {
    date: "OCTOBER 14, 2026",
    title:
      "You Just Started Therapy at 27. That's Not Late. That's the Beginning.",
    href: "/you-just-started-therapy-at-27-thats-not-late",
    category: "Clinical Insights",
  },
  {
    date: "OCTOBER 10, 2026",
    title:
      "The Side Effects You've Stopped Mentioning: Why Some People Look Beyond Medication",
    href: "/side-effects-youve-stopped-mentioning",
    category: "Clinical Insights",
  },
  {
    date: "OCTOBER 10, 2026",
    title:
      "Understanding Your PHQ-9 and GAD-7 Scores (And Why 'Moderate' Can Still Mean Severe)",
    href: "/understanding-your-phq-9-and-gad-7-scores",
    category: "Clinical Insights",
  },
  {
    date: "OCTOBER 10, 2026",
    title:
      "SAINT TMS, Plainly: What It Is, Who It's For, and What Actually Happens",
    href: "/saint-tms-plainly-what-it-is-who-its-for-and-what-happens",
    category: "Treatment Guide",
  },
  {
    date: "OCTOBER 10, 2026",
    title:
      "High-Functioning Depression: When Holding It Together Is the Problem",
    href: "/high-functioning-depression-when-holding-it-together-is-the-problem",
    category: "Clinical Insights",
  },
  {
    date: "OCTOBER 10, 2026",
    title: "How SAINT® Compares to Other Depression Treatment Options",
    href: "/how-saint-compares-to-other-depression-treatment-options",
    category: "Treatment Comparison",
  },
  {
    date: "OCTOBER 10, 2026",
    title:
      '"It Runs in My Family." What That Actually Means for Your Depression',
    href: "/it-runs-in-my-family-what-that-means-for-your-depression",
    category: "Clinical Insights",
  },
  {
    date: "OCTOBER 10, 2026",
    title:
      "Depressed and Anxious at the Same Time? You're Not Confused. You're Typical.",
    href: "/depressed-and-anxious-at-the-same-time",
    category: "Clinical Insights",
  },
  {
    date: "OCTOBER 10, 2026",
    title: "You've Tried Everything. Why Is the Depression Still Here?",
    href: "/youve-tried-everything-why-is-the-depression-still-here",
    category: "Patient Stories",
  },
  {
    date: "OCTOBER 9, 2026",
    title:
      "What Is Accelerated TMS? Understanding 5-Day Protocols vs. Traditional 6-Week Schedules",
    href: "/what-is-accelerated-tms",
    category: "Treatment Science",
  },
  {
    date: "AUGUST 28, 2026",
    title:
      "When Anxiety, Insomnia, and Depression Overlap: Why Treating the Whole Pattern Matters",
    href: "/depression-anxiety-insomnia",
    category: "Clinical Insights",
  },
  {
    date: "AUGUST 14, 2026",
    title:
      "How to Find a Verified SAINT® Provider: 7 Essential Questions to Ask Any Center",
    href: "/find-a-verified-saint-provider",
    category: "Patient Guide",
  },
];

interface NewsAndHappeningsProps {
  heading?: string;
  items?: NewsHeadline[];
  allHeadlinesHref?: string;
  className?: string;
}

export const NewsAndHappenings: React.FC<NewsAndHappeningsProps> = ({
  heading = "Educational Resources",
  items = defaultNewsHeadlines,
  allHeadlinesHref = "/articles",
  className = "",
}) => {
  const [mailingListOpen, setMailingListOpen] = useState(false);
  const [email, setEmail] = useState("");
  const [submitted, setSubmitted] = useState(false);
  const [expanded, setExpanded] = useState(false);

  const INITIAL_COUNT = 6;
  const visibleItems = expanded ? items : items.slice(0, INITIAL_COUNT);
  const hasMore = items.length > INITIAL_COUNT;

  const handleMailingListSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!email.trim()) return;

    const source = "news-mailing-list";
    const formName = "Educational Resources Mailing List";
    const payload = buildTrackingPayload(source, formName);
    payload.formData = {
      email,
    };
    payload.formLabels = {
      email: "Email",
    };

    // Fire-and-forget — never block the UX.
    postTrackingEvent(payload);

    setSubmitted(true);
  };

  return (
    <section
      aria-label={heading}
      className={`relative bg-primary text-primary-foreground py-16 sm:py-20 lg:py-24 overflow-hidden ${className}`}
    >
      {/* Subtle deep navy background accents */}
      <div className="absolute inset-0 bg-gradient-to-b from-primary via-primary-deep/60 to-primary pointer-events-none" />

      <div className="container-px relative mx-auto max-w-6xl px-4 sm:px-6 lg:px-8">
        {/* Header row: title on left, two actions on right (matching reference image) */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 pb-8 sm:pb-12 border-b border-white/15">
          <div>
            <span className="text-xs sm:text-sm font-bold uppercase tracking-widest text-accent mb-2 block">
              Educational Resources & Clinical Articles
            </span>
            <h2 className="font-headings text-3xl sm:text-4xl lg:text-5xl font-extrabold text-white tracking-tight">
              {heading}
            </h2>
          </div>

          <div className="flex flex-wrap items-center gap-3 pt-2 sm:pt-0">
            <Button
              type="button"
              variant="outline"
              onClick={() => {
                setSubmitted(false);
                setMailingListOpen(true);
              }}
              className="border border-white/30 bg-transparent text-white hover:bg-white/10 hover:border-white/60 text-sm font-semibold px-5 py-2.5 rounded-md h-auto transition-colors"
            >
              <Mail className="h-4 w-4 mr-2 opacity-80" />
              Join our mailing list
            </Button>

            <Button
              asChild
              className="bg-medium-blue text-white hover:bg-medium-blue/90 text-sm font-semibold px-5 py-2.5 rounded-md h-auto shadow-sm transition-colors"
            >
              <Link to={allHeadlinesHref}>Explore All Resources</Link>
            </Button>
          </div>
        </div>

        {/* Headlines List: minimal horizontal rows with date, bold title, read article link */}
        <div className="divide-y divide-white/15">
          {visibleItems.map((item, idx) => (
            <article key={idx} className="group py-7 sm:py-9 transition-colors">
              <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
                <div className="space-y-2 max-w-4xl">
                  <div className="flex items-center gap-3">
                    {item.category && (
                      <span className="text-[11px] font-semibold uppercase tracking-wider text-medium-blue">
                        {item.category}
                      </span>
                    )}
                  </div>
                  <h3 className="font-headings text-lg sm:text-xl lg:text-2xl font-bold text-white group-hover:text-accent transition-colors leading-snug">
                    <Link
                      to={item.href}
                      className="focus:outline-hidden focus:underline"
                    >
                      {item.title}
                    </Link>
                  </h3>
                </div>

                <div className="shrink-0 pt-1 lg:pt-0">
                  <Link
                    to={item.href}
                    className="inline-flex items-center gap-2 text-sm font-semibold text-medium-blue group-hover:text-white transition-colors"
                    aria-label={`Read article: ${item.title}`}
                  >
                    <span>Read Article</span>
                    <ArrowRight className="h-4 w-4 group-hover:translate-x-1 transition-transform" />
                  </Link>
                </div>
              </div>
            </article>
          ))}
        </div>

        {/* Show more / Show less toggle */}
        {hasMore && (
          <div className="flex justify-center pt-10">
            <button
              type="button"
              onClick={() => setExpanded((v) => !v)}
              className="inline-flex items-center gap-2 rounded-full border border-white/25 bg-white/5 px-6 py-3 text-sm font-semibold text-white transition-colors hover:bg-white/15 hover:border-accent/60"
              aria-expanded={expanded}
            >
              {expanded ? (
                <>
                  <ChevronUp className="h-4 w-4 text-accent" />
                  Show fewer articles
                </>
              ) : (
                <>
                  <ChevronDown className="h-4 w-4 text-accent" />
                  Show {items.length - INITIAL_COUNT} more articles
                </>
              )}
            </button>
          </div>
        )}
      </div>

      {/* Mailing list dialog */}
      <Dialog open={mailingListOpen} onOpenChange={setMailingListOpen}>
        <DialogContent className="sm:max-w-md bg-card text-card-foreground border-border">
          <DialogHeader>
            <DialogTitle className="font-headings text-2xl font-bold text-primary">
              Join Our Mailing List
            </DialogTitle>
            <DialogDescription className="text-foreground/70 text-sm">
              Receive new clinical research summaries, treatment updates, and
              educational session announcements from Neuro Wellness TMS Centers
              of America.
            </DialogDescription>
          </DialogHeader>

          {submitted ? (
            <div className="py-6 text-center space-y-3">
              <div className="mx-auto w-12 h-12 rounded-full bg-accent/15 text-accent flex items-center justify-center">
                <CheckCircle2 className="h-6 w-6" />
              </div>
              <p className="font-headings text-lg font-bold text-primary">
                Thank you for subscribing
              </p>
              <p className="text-sm text-foreground/70">
                You’ll receive our latest educational updates and research
                releases directly in your inbox.
              </p>
              <Button
                type="button"
                variant="outline"
                onClick={() => setMailingListOpen(false)}
                className="mt-2"
              >
                Close
              </Button>
            </div>
          ) : (
            <form onSubmit={handleMailingListSubmit} className="space-y-4 pt-2">
              <div className="space-y-1.5">
                <label
                  htmlFor="news-email"
                  className="text-sm font-semibold text-primary"
                >
                  Email Address
                </label>
                <Input
                  id="news-email"
                  type="email"
                  required
                  placeholder="you@example.com"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="bg-background text-foreground"
                />
              </div>
              <p className="text-xs text-muted-foreground leading-relaxed">
                We respect your privacy. No spam, ever. Unsubscribe at any time.
              </p>
              <div className="flex justify-end gap-2 pt-2">
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => setMailingListOpen(false)}
                >
                  Cancel
                </Button>
                <Button
                  type="submit"
                  className="bg-gradient-accent text-accent-foreground hover:opacity-90 font-semibold"
                >
                  Subscribe
                </Button>
              </div>
            </form>
          )}
        </DialogContent>
      </Dialog>
    </section>
  );
};

export default NewsAndHappenings;
